import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { agendaLabService } from '../../services/api'

const asArray = (value) => {
  if (Array.isArray(value)) return value
  if (Array.isArray(value?.items)) return value.items
  if (Array.isArray(value?.data)) return value.data
  if (Array.isArray(value?.$values)) return value.$values
  return []
}

const normalizeStatus = (raw) => String(raw || 'scheduled').toLowerCase()

export default function AgendaLabDashboard() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [workload, setWorkload] = useState({
    totalMinutesAvailable: 0,
    totalMinutesOccupied: 0,
    utilizationPercent: 0
  })
  const [appointments, setAppointments] = useState([])

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        setError('')

        const today = new Date().toISOString().slice(0, 10)
        const monthStart = new Date()
        monthStart.setDate(1)
        monthStart.setHours(0, 0, 0, 0)
        const monthEnd = new Date(monthStart)
        monthEnd.setMonth(monthEnd.getMonth() + 1)
        monthEnd.setDate(0)
        monthEnd.setHours(23, 59, 59, 999)

        const [workloadResponse, calendarResponse] = await Promise.all([
          agendaLabService.getWorkload(today),
          agendaLabService.getCalendar(monthStart.toISOString(), monthEnd.toISOString())
        ])

        setWorkload(workloadResponse.data || {
          totalMinutesAvailable: 0,
          totalMinutesOccupied: 0,
          utilizationPercent: 0
        })
        setAppointments(asArray(calendarResponse.data))
      } catch (err) {
        setError(err?.message || 'No se pudo cargar el dashboard.')
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [])

  const stats = useMemo(() => {
    const occupiedHours = Number(((workload.totalMinutesOccupied || 0) / 60).toFixed(1))
    const availableMinutes = workload.totalMinutesAvailable || 0
    const freeHours = Number((Math.max(0, availableMinutes - (workload.totalMinutesOccupied || 0)) / 60).toFixed(1))
    const activeAppointments = appointments.filter((a) => normalizeStatus(a.status) !== 'cancelled')
    return {
      occupiedHours,
      freeHours,
      scheduledCount: activeAppointments.length
    }
  }, [workload, appointments])

  const statusChart = useMemo(() => {
    const counts = {
      completed: 0,
      scheduled: 0,
      rescheduled: 0,
      cancelled: 0
    }

    appointments.forEach((appointment) => {
      const key = normalizeStatus(appointment.status)
      if (key in counts) counts[key] += 1
      else counts.scheduled += 1
    })

    const max = Math.max(1, ...Object.values(counts))
    return [
      { key: 'completed', label: 'Cumplidas', color: 'bg-emerald-500', value: counts.completed, width: (counts.completed / max) * 100 },
      { key: 'scheduled', label: 'Agendadas', color: 'bg-blue-500', value: counts.scheduled, width: (counts.scheduled / max) * 100 },
      { key: 'rescheduled', label: 'Reprogramadas', color: 'bg-amber-500', value: counts.rescheduled, width: (counts.rescheduled / max) * 100 },
      { key: 'cancelled', label: 'Canceladas', color: 'bg-rose-500', value: counts.cancelled, width: (counts.cancelled / max) * 100 }
    ]
  }, [appointments])

  const recentClients = useMemo(() => {
    const sorted = [...appointments]
      .filter((a) => a?.client?.fullName)
      .sort((a, b) => new Date(b.startAtUtc || 0) - new Date(a.startAtUtc || 0))

    const uniqueByClient = []
    const seen = new Set()
    for (const appointment of sorted) {
      const key = appointment.clientId || appointment.client?.id || `${appointment.client?.fullName}-${appointment.id}`
      if (seen.has(key)) continue
      seen.add(key)
      uniqueByClient.push(appointment)
      if (uniqueByClient.length === 5) break
    }

    return uniqueByClient
  }, [appointments])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard AgendaLab</h1>
        <p className="text-gray-600">Estado operativo de tus citas y clientes.</p>
      </div>

      {error && <div className="card p-3 text-sm text-red-700 bg-red-50 border border-red-200">{error}</div>}

      {loading ? (
        <div className="card p-6 text-sm text-gray-600">Cargando indicadores...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card p-5">
              <p className="text-sm text-gray-500">Horas Ocupadas</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.occupiedHours}</p>
            </div>
            <div className="card p-5">
              <p className="text-sm text-gray-500">Horas Libres</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.freeHours}</p>
            </div>
            <div className="card p-5">
              <p className="text-sm text-gray-500">Citas Agendadas</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.scheduledCount}</p>
            </div>
          </div>

          <div className="card p-5">
            <h2 className="text-lg font-semibold text-gray-900">Estado de Citas</h2>
            <div className="mt-4 space-y-3">
              {statusChart.map((row) => (
                <div key={row.key}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-700">{row.label}</span>
                    <span className="font-medium text-gray-900">{row.value}</span>
                  </div>
                  <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`${row.color} h-full rounded-full`} style={{ width: `${row.width}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card p-5">
            <h2 className="text-lg font-semibold text-gray-900">5 Clientes Más Recientes</h2>
            <div className="mt-3 space-y-3">
              {recentClients.length === 0 && (
                <p className="text-sm text-gray-500">Aún no hay clientes con citas registradas.</p>
              )}
              {recentClients.map((appointment) => (
                <div key={appointment.id} className="flex flex-col md:flex-row md:items-center md:justify-between border-b pb-3">
                  <div>
                    <p className="font-medium text-gray-900">{appointment.client?.fullName}</p>
                    <p className="text-sm text-gray-500">{appointment.client?.email || 'Sin email registrado'}</p>
                    <p className="text-sm text-gray-600">
                      {appointment.service?.name || 'Servicio'} - {new Date(appointment.startAtUtc).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">
                      {appointment.notes ? `Descripción: ${appointment.notes}` : 'Sin descripción registrada.'}
                    </p>
                  </div>
                  <Link
                    to={`/agendalab/calendar#appointment-${appointment.id}`}
                    className="text-sm font-medium text-primary-600 hover:text-primary-700 mt-2 md:mt-0"
                  >
                    Ver descripción de cita
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
